/* ============================================================
   nabi theme — global behaviour
   header · mobile menu · reveals · marquee · parallax · atelier
   quote · newsletter · accordions · cart drawer (Section Rendering API)
   Founder's rule: no prefers-reduced-motion guards. Motion stays on,
   kept gentle instead.
   ============================================================ */
(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => [...(r || document).querySelectorAll(s)];
  const routes = window.nabiRoutes || { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update' };

  window.nabi = window.nabi || {};

  /* ---------- reveals (also usable for injected nodes) ---------- */
  const io = new IntersectionObserver(entries => {
    for (const e of entries) if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
  }, { threshold: .12, rootMargin: '0px 0px -40px 0px' });
  const observe = root => $$('[data-reveal]', root || document).forEach(el => { if (!el.classList.contains('in')) io.observe(el); });
  window.nabi.reveal = observe;
  const revealAllNow = root => requestAnimationFrame(() => requestAnimationFrame(() => $$('[data-reveal]', root || document).forEach(el => el.classList.add('in'))));
  window.nabi.revealAll = revealAllNow;

  const init = root => {
    root = root || document;

    /* header state */
    const header = $('.site-header');
    if (header && !header.dataset.bound) {
      header.dataset.bound = '1';
      const onScroll = () => header.classList.toggle('scrolled', scrollY > 10);
      addEventListener('scroll', onScroll, { passive: true }); onScroll();
    }

    /* mobile menu */
    const burger = $('#burger'), mmenu = $('#mmenu');
    if (burger && mmenu && !burger.dataset.bound) {
      burger.dataset.bound = '1';
      const setOpen = open => {
        mmenu.classList.toggle('open', open);
        mmenu.setAttribute('aria-hidden', !open);
        burger.classList.toggle('x', open);
        burger.setAttribute('aria-expanded', open);
        document.body.classList.toggle('locked', open);
        if (open) $$('a', mmenu).forEach((a, i) => a.style.transitionDelay = (0.06 * i + 0.08) + 's');
      };
      burger.addEventListener('click', () => setOpen(!mmenu.classList.contains('open')));
      $$('a', mmenu).forEach(a => a.addEventListener('click', () => setOpen(false)));
      addEventListener('keydown', e => { if (e.key === 'Escape') setOpen(false); });
    }

    /* reveals: product pages reveal everything on load; everything else waits for scroll */
    if (document.body.classList.contains('reveal-all')) revealAllNow(root); else observe(root);

    /* marquee: double the track until one half is wider than the viewport, ~42px/s */
    $$('.marquee .track', root).forEach(track => {
      if (track.dataset.bound) return; track.dataset.bound = '1';
      const base = track.innerHTML;
      const speed = parseFloat(track.dataset.speed) || 42;
      const fill = () => {
        track.innerHTML = base;
        let guard = 0;
        while (track.scrollWidth < track.parentElement.offsetWidth * 2 && guard++ < 8) track.innerHTML += track.innerHTML;
        track.style.animationDuration = (track.scrollWidth / 2) / speed + 's';
      };
      fill();
      addEventListener('resize', fill);
    });

    /* hero parallax — desktop only; the mobile hero is a swipe carousel */
    const shots = $$('[data-parallax]', root);
    if (shots.length && !document.body.dataset.parallaxBound) {
      document.body.dataset.parallaxBound = '1';
      const wide = matchMedia('(min-width: 921px)');
      let ticking = false;
      const move = () => {
        const y = scrollY;
        for (const s of $$('.shot[data-parallax]')) s.style.transform = wide.matches ? `translateY(${y * parseFloat(s.dataset.parallax)}px)` : '';
        ticking = false;
      };
      addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(move); ticking = true; } }, { passive: true });
      wide.addEventListener('change', move);
    }

    /* tone atelier — hover/click a row re-dyes the stage; gentle auto-rotate until first touch */
    $$('.atelier', root).forEach(atelier => {
      if (atelier.dataset.bound) return; atelier.dataset.bound = '1';
      const stage = $('.stagecard', atelier);
      const sc = { num: $('.num', stage), name: $('h3', stage), story: $('.sstory', stage), read: $('.acts .read', stage), go: $('.acts .go', stage), soon: $('.soonb', stage) };
      const rows = $$('.trow', atelier);
      const rotating = rows.filter(r => !r.dataset.tease);
      let timer = null, idx = 0, swapTimer = null;
      const stopAuto = () => { if (timer) { clearInterval(timer); timer = null; } };
      const apply = row => {
        rows.forEach(x => x.classList.toggle('on', x === row));
        stage.classList.add('fading');
        clearTimeout(swapTimer);
        swapTimer = setTimeout(() => {
          stage.style.backgroundColor = row.dataset.color;
          stage.classList.toggle('dark', row.dataset.dark === '1');
          if (sc.num) sc.num.textContent = row.dataset.num || '';
          if (sc.name) sc.name.textContent = row.dataset.name || '';
          if (sc.story) sc.story.textContent = row.dataset.story || '';
          if (sc.read) { sc.read.href = row.dataset.href || '#'; sc.read.style.display = row.dataset.href ? '' : 'none'; }
          if (sc.go) { sc.go.href = row.dataset.shop || '#'; }
          if (sc.soon) sc.soon.textContent = row.dataset.soon || sc.soon.textContent;
          stage.classList.toggle('tease', !!row.dataset.tease);
          stage.classList.toggle('raining', !!row.dataset.rain);
          stage.classList.remove('fading');
        }, 280);
      };
      rows.forEach((r, i) => {
        ['mouseenter', 'click', 'focus'].forEach(ev => r.addEventListener(ev, () => { stopAuto(); idx = rotating.indexOf(r); apply(r); }));
      });
      const every = parseInt(atelier.dataset.interval || '4800', 10);
      if (rotating.length > 1 && !document.body.classList.contains('shopify-design-mode')) {
        timer = setInterval(() => { idx = (idx + 1) % rotating.length; apply(rotating[idx]); }, every);
      }
    });

    /* quote — word by word reveal */
    $$('[data-quote]', root).forEach(q => {
      if (q.dataset.bound) return; q.dataset.bound = '1';
      q.innerHTML = q.textContent.trim().split(/\s+/).map((w, i) => `<span class="w" style="--i:${i}">${w}</span>`).join(' ');
      new IntersectionObserver((es, obs) => { for (const e of es) if (e.isIntersecting) { q.classList.add('in'); obs.disconnect(); } }, { threshold: .4 }).observe(q);
    });

    /* accordions */
    $$('.acc', root).forEach(acc => {
      if (acc.dataset.bound) return; acc.dataset.bound = '1';
      const btn = $(':scope > button', acc), body = $(':scope > .body', acc);
      if (!btn || !body) return;
      const set = open => { acc.classList.toggle('open', open); btn.setAttribute('aria-expanded', open); body.style.maxHeight = open ? body.scrollHeight + 'px' : 0; };
      set(acc.classList.contains('open'));
      btn.addEventListener('click', () => set(!acc.classList.contains('open')));
      addEventListener('resize', () => { if (acc.classList.contains('open')) body.style.maxHeight = body.scrollHeight + 'px'; });
    });

    /* newsletter — the form posts to Shopify; add a soft loading state */
    $$('.emailform', root).forEach(f => f.addEventListener('submit', () => f.classList.add('loading')));

    /* bag links open the drawer */
    $$('[data-cart-open]', root).forEach(a => {
      if (a.dataset.bound) return; a.dataset.bound = '1';
      a.addEventListener('click', e => { if (window.nabi.cartDrawerEnabled) { e.preventDefault(); openCart(); } });
    });
  };

  /* ---------- toast ---------- */
  let toastTimer;
  const toast = msg => {
    let t = $('.toast');
    if (!t) { t = document.createElement('div'); t.className = 'toast'; document.body.appendChild(t); }
    t.textContent = msg; requestAnimationFrame(() => t.classList.add('show'));
    clearTimeout(toastTimer); toastTimer = setTimeout(() => t.classList.remove('show'), 2400);
  };
  window.nabi.toast = toast;

  /* ---------- cart ---------- */
  const bumpCount = n => {
    $$('[data-cart-count]').forEach(c => { c.textContent = n; c.classList.add('pop'); setTimeout(() => c.classList.remove('pop'), 350); });
  };
  const openCart = () => {
    const d = $('#cart-drawer'); if (!d) { location.href = routes.cart; return; }
    document.body.classList.add('cart-open'); d.setAttribute('aria-hidden', 'false');
    const first = $('.cd-close', d); if (first) first.focus();
  };
  const closeCart = () => { document.body.classList.remove('cart-open'); const d = $('#cart-drawer'); if (d) d.setAttribute('aria-hidden', 'true'); };
  window.nabi.openCart = openCart; window.nabi.closeCart = closeCart;

  const renderDrawer = html => {
    const d = $('#cart-drawer'); if (!d || !html) return;
    const tmp = document.createElement('div'); tmp.innerHTML = html;
    const fresh = tmp.querySelector('#cart-drawer');
    if (fresh) { d.innerHTML = fresh.innerHTML; bindDrawer(); }
  };
  const refreshDrawer = async () => {
    try {
      const r = await fetch(`${routes.root || '/'}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
      const j = await r.json(); renderDrawer(j['cart-drawer']);
    } catch (e) { /* silent */ }
  };
  const readCount = j => (j && typeof j.item_count === 'number') ? j.item_count : null;

  /* add one or more items; items = [{id, quantity, properties}] */
  const addToCart = async (items, opts) => {
    opts = opts || {};
    const body = { items, sections: 'cart-drawer', sections_url: routes.root || '/' };
    const r = await fetch(routes.cartAdd + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.description || j.message || 'Could not add to bag');
    if (j.sections && j.sections['cart-drawer']) renderDrawer(j.sections['cart-drawer']);
    const cart = await (await fetch(routes.cart + '.js')).json();
    bumpCount(cart.item_count);
    if (opts.open !== false && window.nabi.cartDrawerEnabled) openCart();
    document.dispatchEvent(new CustomEvent('nabi:cart', { detail: cart }));
    return cart;
  };
  window.nabi.addToCart = addToCart;

  const changeLine = async (key, quantity) => {
    const r = await fetch(routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: routes.root || '/' }) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.description || j.message || 'Could not update bag');
    if (j.sections && j.sections['cart-drawer']) renderDrawer(j.sections['cart-drawer']);
    bumpCount(readCount(j));
    document.dispatchEvent(new CustomEvent('nabi:cart', { detail: j }));
    return j;
  };
  window.nabi.changeLine = changeLine;

  const bindDrawer = () => {
    const d = $('#cart-drawer'); if (!d) return;
    $$('[data-cart-close]', d).forEach(b => b.addEventListener('click', closeCart));
    $$('[data-line-qty]', d).forEach(input => {
      const line = input.closest('.cline');
      const key = input.dataset.lineQty;
      const set = async q => {
        line.classList.add('busy');
        try { await changeLine(key, q); } catch (e) { toast(e.message); line.classList.remove('busy'); }
      };
      $$('[data-qty-minus]', line).forEach(b => b.addEventListener('click', () => set(Math.max(0, (parseInt(input.value, 10) || 1) - 1))));
      $$('[data-qty-plus]', line).forEach(b => b.addEventListener('click', () => set((parseInt(input.value, 10) || 0) + 1)));
      input.addEventListener('change', () => set(Math.max(0, parseInt(input.value, 10) || 0)));
      $$('[data-line-remove]', line).forEach(b => b.addEventListener('click', () => set(0)));
    });
  };
  const overlay = $('.cart-overlay'); if (overlay) overlay.addEventListener('click', closeCart);
  addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });
  bindDrawer();

  /* cart page quantity controls share the same markup */
  $$('.cartlist [data-line-qty]').forEach(input => {
    const line = input.closest('.cline'); const key = input.dataset.lineQty;
    input.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); input.blur(); } });
    const set = async q => {
      line.classList.add('busy');
      try { await changeLine(key, q); location.reload(); } catch (e) { toast(e.message); line.classList.remove('busy'); }
    };
    $$('[data-qty-minus]', line).forEach(b => b.addEventListener('click', () => set(Math.max(0, (parseInt(input.value, 10) || 1) - 1))));
    $$('[data-qty-plus]', line).forEach(b => b.addEventListener('click', () => set((parseInt(input.value, 10) || 0) + 1)));
    input.addEventListener('change', () => set(Math.max(0, parseInt(input.value, 10) || 0)));
    $$('[data-line-remove]', line).forEach(b => b.addEventListener('click', () => set(0)));
  });

  /* generic one-tap add buttons: <button data-add-variant="123" data-qty="1"> */
  document.addEventListener('click', async e => {
    const b = e.target.closest('[data-add-variant]'); if (!b) return;
    e.preventDefault();
    const id = parseInt(b.dataset.addVariant, 10); if (!id) { toast(b.dataset.addMissing || 'This piece is not linked to a product yet'); return; }
    b.classList.add('loading');
    try {
      await addToCart([{ id, quantity: parseInt(b.dataset.qty || '1', 10) }], { open: b.dataset.open !== 'false' });
      if (b.dataset.addedText) { b.classList.add('on'); b.textContent = b.dataset.addedText; }
    } catch (err) { toast(err.message); }
    b.classList.remove('loading');
  });

  /* ---------- boot + theme editor hooks ---------- */
  init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:select', e => { if (e.target.querySelector('#cart-drawer')) openCart(); });
  document.addEventListener('shopify:section:deselect', e => { if (e.target.querySelector('#cart-drawer')) closeCart(); });
})();
